/**
 * @deprecated Use `PaymentMethodCard` from `@/components/residents/redesign/payments` instead.
 * This component uses the old photo-background card design and will be removed after full migration.
 */
import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ViewStyle,
  Image,
  ImageBackground,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { formatCardExpiry, isPaymentMethodBank } from '@/utils/residents/paymentUtils';
import type { PaymentMethod } from '@/types/residents/payment';
import PaymentMethodCardHeader from './PaymentMethodCardHeader';
import PaymentMethodCardBody from './PaymentMethodCardBody';

const TrashIconAdmin = SVG_COMPONENTS.TRASH;

// Card brand icons
const CardIcons = RESIDENTS_SVG_COMPONENTS.cards;
const BankIcon = RESIDENTS_SVG_COMPONENTS.BANK;

// Card background images
const CARD_BACKGROUNDS = [
  require('@/assets/residents/cards/backgrounds/1-2x.png'),
  require('@/assets/residents/cards/backgrounds/2-2x.png'),
  require('@/assets/residents/cards/backgrounds/3-2x.png'),
  require('@/assets/residents/cards/backgrounds/4-2x.png'),
  require('@/assets/residents/cards/backgrounds/5-2x.png'),
  require('@/assets/residents/cards/backgrounds/6-2x.png'),
  require('@/assets/residents/cards/backgrounds/7-2x.png'),
];

type Variant = 'desktop' | 'mobile' | 'wallet';

interface PaymentMethodCardProps {
  /** Payment method data - can be raw API response or normalized PaymentMethod */
  method: any;
  /** Index for card background color cycling */
  index?: number;
  /** Display variant */
  variant?: Variant;
  /** Whether this card is selected */
  isSelected?: boolean;
  /** Whether the card is expired */
  isExpired?: boolean;
  /** Whether the card is blocked */
  isBlocked?: boolean;
  /** Whether this is the only card (affects display) */
  isOnlyCard?: boolean;
  /** Handler for selecting this card */
  onSelect?: () => void;
  /** Handler for setting as default */
  onSetAsDefault?: () => void;
  /** Handler for deleting */
  onDelete?: () => void;
  /** Whether an update is in progress */
  isUpdating?: boolean;
  /** Whether delete is allowed */
  canDelete?: boolean;
  /** Whether card has autopay attached */
  hasAutoPay?: boolean;
  /** Whether to show action buttons */
  showActions?: boolean;
  /** Whether this card is disabled (expired/blocked) */
  disabled?: boolean;
  /** Custom container style */
  style?: ViewStyle;
  
  // Mobile wallet props (can override method values)
  type?: 'card' | 'bank' | 'ach';
  last4?: string;
  brand?: string;
  bankName?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault?: boolean;
}

/**
 * Unified Payment Method Card Component
 * Displays credit card or ACH bank account info with visual styling
 * Supports desktop (full card), mobile (compact), and wallet (visual card) variants
 */
export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  method,
  index = 0,
  variant = Platform.OS === 'web' ? 'desktop' : 'wallet',
  isSelected = false,
  isExpired: isExpiredProp,
  isBlocked = false,
  isOnlyCard = false,
  onSelect,
  onSetAsDefault,
  onDelete,
  isUpdating = false,
  canDelete = true,
  hasAutoPay = false,
  showActions = true,
  disabled: disabledProp = false,
  style,
  type: typeProp,
  last4: last4Prop,
  brand: brandProp,
  bankName: bankNameProp,
  expiryMonth: expiryMonthProp,
  expiryYear: expiryYearProp,
  isDefault: isDefaultProp,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const DESKTOP_COLORS = useDesktopColors();
  const { semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  // Normalize props
  const type = typeProp || method?.type || 'card';
  const last4 = last4Prop || method?.last4 || '';
  const brand = brandProp || method?.brand || method?.accttype;
  const bankName = bankNameProp || method?.bankName || method?.name;
  const expiryMonth = expiryMonthProp || method?.expiryMonth;
  const expiryYear = expiryYearProp || method?.expiryYear;
  const isDefault = isDefaultProp ?? method?.isDefault ?? false;
  const isExpired = isExpiredProp ?? method?.isExpired ?? false;
  
  const isDisabled = disabledProp || isExpired || isBlocked;
  const canSetDefault = !isDefault && !isDisabled && onSetAsDefault;
  const showDeleteTooltip = !canDelete && (isDefault || hasAutoPay);
  
  const isBank = isPaymentMethodBank(method) || type === 'bank' || type === 'ach';
  const isCardType = !isBank;

  const formatExpiry = (): string => {
    // First try expiryMonth/expiryYear
    if (expiryMonth && expiryYear) {
      const month = expiryMonth.toString().padStart(2, '0');
      const year = expiryYear.toString().slice(-2);
      return `${month}/${year}`;
    }
    
    // Fall back to parsing method.expiry string (MMYY or MM/YY format)
    const expiryStr = method?.expiry;
    if (!expiryStr) return '';
    
    if (expiryStr.includes('/')) {
      // Already in MM/YY format
      return expiryStr;
    } else if (expiryStr.length === 4) {
      // MMYY format - convert to MM/YY
      return `${expiryStr.substring(0, 2)}/${expiryStr.substring(2)}`;
    }
    
    return '';
  };

  const getBrandIcon = (size: 'small' | 'large' = 'small') => {
    if (!brand) return null;
    const brandUpper = (brand || '').toUpperCase();
    // Figma specs: 32x20 for multi-card (small), 48x30 for single-card (large)
    const iconSize = size === 'large' ? { width: 48, height: 30 } : { width: 32, height: 20 };
    
    const brandIconMap: Record<string, any> = {
      'VISA': CardIcons.VISA,
      'MC': CardIcons.MASTERCARD,
      'MASTERCARD': CardIcons.MASTERCARD,
      'AMEX': CardIcons.AMEX,
      'AMERICAN EXPRESS': CardIcons.AMEX,
      'DISC': CardIcons.DISCOVER,
      'DISCOVER': CardIcons.DISCOVER,
      'DNR': CardIcons.DINERS,
      'DINERS': CardIcons.DINERS,
      'DINERS CLUB': CardIcons.DINERS,
      'JCB': CardIcons.JCB,
      'MAES': CardIcons.MAESTRO,
      'MAESTRO': CardIcons.MAESTRO,
      'UP': CardIcons.UNIONPAY,
      'UNIONPAY': CardIcons.UNIONPAY,
    };
    
    const IconComponent = brandIconMap[brandUpper];
    if (IconComponent) {
      return <IconComponent {...iconSize} />;
    }
    return null;
  };

  const getCardType = (accttype: string) => {
    const cardTypes: Record<string, { name: string; icon: React.ReactNode; isBank: boolean }> = {
      'VISA': { name: 'Visa', icon: <CardIcons.VISA width={32} height={20} />, isBank: false },
      'MC': { name: 'MasterCard', icon: <CardIcons.MASTERCARD width={32} height={20} />, isBank: false },
      'AMEX': { name: 'American Express', icon: <CardIcons.AMEX width={32} height={20} />, isBank: false },
      'DISC': { name: 'Discover', icon: <CardIcons.DISCOVER width={32} height={20} />, isBank: false },
      'DNR': { name: "Diner's Club", icon: <CardIcons.DINERS width={32} height={20} />, isBank: false },
      'JCB': { name: 'Japan Credit Bureau', icon: <CardIcons.JCB width={32} height={20} />, isBank: false },
      'UP': { name: 'Union Pay', icon: <CardIcons.UNIONPAY width={32} height={20} />, isBank: false },
      'MAES': { name: 'Maestro', icon: <CardIcons.MAESTRO width={32} height={20} />, isBank: false },
      'ECHK': { name: 'Bank Account', icon: null, isBank: true },
      'ESAV': { name: 'Bank Account', icon: null, isBank: true },
      'PBEBIT': { name: 'Bank Account', icon: null, isBank: true },
      'bankAccount': { name: 'Bank Account', icon: null, isBank: true },
    };
    return cardTypes[accttype] || cardTypes['DNR'];
  };

  const getCardBackgroundColor = (idx: number): string => {
    const gradients = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#43e97b', '#fa709a'];
    return gradients[idx % gradients.length];
  };

  const renderDeleteButton = (isMobileStyle: boolean = false) => {
    if (!showActions || !onDelete) return null;

    const IconComponent = isMobileStyle ? TrashIconAdmin : RESIDENTS_SVG_COMPONENTS.TRASH;
    const iconColor = !canDelete
      ? (isMobileStyle ? '#3F3F3F' : DESKTOP_COLORS.TEXT_TERTIARY)
      : (isMobileStyle ? '#5E5E5E' : semantic.status.error);
    
    return (
      <TouchableOpacity 
        onPress={canDelete ? onDelete : undefined}
        disabled={!canDelete || (isMobileStyle && isDefault)}
        style={[styles.deleteButton, !canDelete && styles.deleteButtonDisabled]}
        activeOpacity={0.7}
      >
        {IconComponent && <IconComponent width={isMobileStyle ? 16 : 20} height={isMobileStyle ? 16 : 20} color={iconColor} />}
      </TouchableOpacity>
    );
  };

  const renderStatusBadge = (isMobileStyle: boolean = false) => {
    if (isExpired) {
      return (
        <View style={isMobileStyle ? styles.expiredBadgeMobile : styles.expiredBadgeDesktop}>
          <Text style={isMobileStyle ? [styles.expiredBadgeTextMobile, { color: MARKETPLACE_COLORS.dangerRed }] : styles.expiredBadgeTextDesktop}>Expired</Text>
        </View>
      );
    }

    if (isDefault) {
      return (
        <View style={isMobileStyle ? styles.defaultBadgeMobile : [styles.defaultBadgeDesktop, { backgroundColor: DESKTOP_COLORS.BORDER_SECONDARY }]}>
          <Text style={isMobileStyle ? [styles.defaultBadgeTextMobile, { color: MARKETPLACE_COLORS.textPrimary }] : [styles.defaultBadgeTextDesktop, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>Default</Text>
        </View>
      );
    }

    if (canSetDefault) {
      return (
        <TouchableOpacity onPress={onSetAsDefault} disabled={isUpdating} activeOpacity={0.7}>
          <Text style={isMobileStyle ? [styles.setDefaultTextMobile, { color: MARKETPLACE_COLORS.accentBlueSolid }] : styles.setDefaultTextDesktop}>Make default</Text>
        </TouchableOpacity>
      );
    }

    return isMobileStyle ? <View style={styles.spacerMobile} /> : null;
  };

  // ==================== WALLET VARIANT ====================
  if (variant === 'wallet') {
    const backgroundImage = CARD_BACKGROUNDS[index % CARD_BACKGROUNDS.length];
    
    // Single card mode - show expanded layout with labels
    if (isOnlyCard) {
      return (
        <View style={[styles.cardWrapperMobile, styles.cardWrapperSingle, style]}>
          <View style={styles.cardMobile}>
            <ImageBackground
              source={backgroundImage}
              style={styles.topSectionSingle}
              imageStyle={styles.backgroundImage}
              resizeMode="cover"
            >
              <View style={styles.gradientOverlayMobile} />
              
              {isCardType ? (
                <>
                  {/* Brand icon - larger for single card */}
                  <View style={styles.brandContainerSingle}>
                    {getBrandIcon('large') || (
                      <Text style={[styles.brandTextMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>{brand || 'Card'}</Text>
                    )}
                  </View>

                  {/* Card details with labels */}
                  <View style={styles.cardDetailsSingle}>
                    {/* Labels row */}
                    <View style={styles.labelsRow}>
                      <Text style={[styles.labelText, { color: MARKETPLACE_COLORS.textPrimary }]}>Cardholder Name</Text>
                      <Text style={[styles.labelText, { color: MARKETPLACE_COLORS.textPrimary }]}>Expiry</Text>
                    </View>
                    {/* Values row */}
                    <View style={styles.valuesRow}>
                      <Text style={[styles.cardNumberSingle, { color: MARKETPLACE_COLORS.textPrimary }]}>**** **** **** {last4}</Text>
                      <Text style={[styles.expirySingle, { color: MARKETPLACE_COLORS.textPrimary }]}>{formatExpiry()}</Text>
                    </View>
                  </View>
                </>
              ) : (
                <>
                  {/* ACH header - bank icon + bank name */}
                  <View style={styles.achHeaderSingle}>
                    <View style={styles.bankIconContainerSingle}>
                      {BankIcon && <BankIcon width={30} height={30} color={MARKETPLACE_COLORS.textPrimary} />}
                    </View>
                    <Text style={[styles.bankNameSingle, { color: MARKETPLACE_COLORS.textPrimary }]} numberOfLines={2}>{bankName || 'Bank Name'}</Text>
                  </View>

                  {/* Account details */}
                  <View style={styles.achDetailsSingle}>
                    <Text style={[styles.labelText, { color: MARKETPLACE_COLORS.textPrimary }]}>Account Holder Name</Text>
                    <Text style={[styles.cardNumberSingle, { color: MARKETPLACE_COLORS.textPrimary }]}>**** **** **** {last4}</Text>
                  </View>
                </>
              )}
            </ImageBackground>

            <View style={styles.bottomSectionMobile}>
              {renderStatusBadge(true)}
              {renderDeleteButton(true)}
            </View>
          </View>
        </View>
      );
    }
    
    // Multi-card mode - compact layout
    return (
      <View style={[styles.cardWrapperMobile, style]}>
        <View style={styles.cardMobile}>
          <ImageBackground
            source={backgroundImage}
            style={styles.topSectionMobile}
            imageStyle={styles.backgroundImage}
            resizeMode="cover"
          >
            <View style={styles.gradientOverlayMobile} />
            
            {isCardType ? (
              <>
                <View style={styles.brandContainerMobile}>
                  {getBrandIcon('small') || (
                    <Text style={[styles.brandTextMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>{brand || 'Card'}</Text>
                  )}
                </View>
                <View style={styles.cardDetailsMobile}>
                  <Text style={[styles.last4TextMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>*{last4}</Text>
                  <Text style={[styles.expiryTextMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>{formatExpiry()}</Text>
                </View>
              </>
            ) : (
              <View style={styles.achContentMobile}>
                <View style={styles.achHeaderMobile}>
                  <View style={styles.bankIconContainerMobile}>
                    {BankIcon && <BankIcon width={20} height={20} color={MARKETPLACE_COLORS.textPrimary} />}
                  </View>
                  <Text style={[styles.last4TextMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>*{last4}</Text>
                </View>
                {bankName && (
                  <Text style={[styles.bankNameTextMobile, { color: MARKETPLACE_COLORS.textPrimary }]} numberOfLines={2}>{bankName}</Text>
                )}
              </View>
            )}
          </ImageBackground>

          <View style={styles.bottomSectionMobile}>
            {renderStatusBadge(true)}
            {renderDeleteButton(true)}
          </View>
        </View>
      </View>
    );
  }

  // ==================== DESKTOP VARIANT ====================
  const cardType = getCardType(method?.accttype || type);
  const formattedExpiry = formatCardExpiry(method?.expiry);
  const cardBackgroundIndex = index % 7;

  return (
    <View style={[
      styles.cardContainerDesktop,
      isOnlyCard && styles.cardContainerWideDesktop,
      Platform.OS === 'web' && { backgroundColor: DESKTOP_COLORS.SECONDARY_BACKGROUND },
      style,
    ]}>
      <View style={[
        styles.cardVisualDesktop,
        { backgroundColor: getCardBackgroundColor(cardBackgroundIndex) },
        isOnlyCard && styles.cardVisualWideDesktop,
      ]}>
        <PaymentMethodCardHeader cardType={cardType as any} last4={last4} isOnlyCard={isOnlyCard} />
        <PaymentMethodCardBody cardType={cardType as any} method={method} isOnlyCard={isOnlyCard} formattedExpiry={formattedExpiry} />
      </View>

      <View style={[styles.cardActionsFooterDesktop, { backgroundColor: DESKTOP_COLORS.SECONDARY_BACKGROUND }]}>
        <View style={styles.cardStatusBadgesDesktop}>
          {renderStatusBadge(false)}
        </View>
        {renderDeleteButton(false)}
      </View>

      {showDeleteTooltip && (
        <Text style={[styles.tooltipTextDesktop, { color: DESKTOP_COLORS.TEXT_TERTIARY, backgroundColor: DESKTOP_COLORS.SECONDARY_BACKGROUND }]}>
          {isDefault
            ? "This payment method is the default and cannot be deleted. Please change your default method in order to delete this one."
            : "This payment method has a recurring payment attached that must be removed before it can be deleted"
          }
        </Text>
      )}
    </View>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  // Desktop styles
  cardContainerDesktop: {
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
    width: '100%',
  } as any,
  cardContainerWideDesktop: { width: '100%' },
  cardVisualDesktop: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    aspectRatio: 2.0,
    justifyContent: 'space-between',
    borderRadius: 12,
    position: 'relative',
    minHeight: 84,
  },
  cardVisualWideDesktop: { aspectRatio: Platform.OS === 'web' ? 3.0 : 1.8 },
  cardActionsFooterDesktop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    minHeight: 40,
  },
  cardStatusBadgesDesktop: { flexDirection: 'row', alignItems: 'center' },
  defaultBadgeDesktop: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 60,
  },
  defaultBadgeTextDesktop: { fontSize: 12, fontWeight: '500' },
  expiredBadgeDesktop: {
    backgroundColor: 'rgba(255, 62, 62, 0.1)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 60,
  },
  expiredBadgeTextDesktop: { fontSize: 12, fontWeight: '500', color: '#FF3E3E' },
  setDefaultTextDesktop: { fontSize: 12, fontWeight: '500', color: semantic.interactive.primary },
  deleteButton: { width: 20, height: 20, alignItems: 'center', justifyContent: 'center' },
  deleteButtonDisabled: { opacity: 0.5 },
  tooltipTextDesktop: {
    fontSize: 10,
    fontStyle: 'italic',
    paddingHorizontal: 16,
    marginBottom: 8,
    lineHeight: 14,
  },

  // Mobile/Wallet styles
  cardWrapperMobile: { flex: 1 },
  cardWrapperSingle: { flex: 1 },
  cardMobile: { backgroundColor: '#2F2F2F', borderRadius: 8, overflow: 'hidden' },
  backgroundImage: { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
  // Figma: p-[16px] gap-[24px] for multi-card credit cards
  topSectionMobile: {
    padding: 16,
    gap: 24,
    position: 'relative',
  },
  // Single card expanded layout - Figma: p-[16px] gap-[56px]
  topSectionSingle: {
    padding: 16,
    gap: 56,
    position: 'relative',
  },
  gradientOverlayMobile: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(23, 23, 23, 0.5)',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  brandContainerMobile: { zIndex: 1 },
  brandContainerSingle: { zIndex: 1 },
  brandTextMobile: { fontSize: 14, fontWeight: '600' },
  cardDetailsMobile: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1,
  },
  // Single card details with labels
  cardDetailsSingle: {
    gap: 6,
    zIndex: 1,
  },
  labelsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  valuesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  labelText: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  cardNumberSingle: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 27,
  },
  expirySingle: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 27,
    textAlign: 'right' as const,
  },
  // ACH single card styles
  achHeaderSingle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1,
  },
  bankIconContainerSingle: {
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bankNameSingle: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 27,
    textAlign: 'right' as const,
    flex: 1,
    marginLeft: 16,
  },
  achDetailsSingle: {
    gap: 6,
    zIndex: 1,
  },
  last4TextMobile: { fontSize: 14, fontWeight: '400', lineHeight: 21 },
  expiryTextMobile: { fontSize: 14, fontWeight: '400', lineHeight: 21, textAlign: 'right' as const },
  // Figma: ACH multi-card uses gap-[8px]
  achContentMobile: { gap: 8, zIndex: 1 },
  achHeaderMobile: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  bankIconContainerMobile: { width: 24, height: 24, alignItems: 'center', justifyContent: 'center' },
  // Figma: text-[12px], font-normal, line-height 1.5, h-[36px] for 2 lines
  bankNameTextMobile: { fontSize: 12, fontWeight: '400', lineHeight: 18, minHeight: 36 },
  // Figma: px-[16px] py-[14px]
  bottomSectionMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  defaultBadgeMobile: {
    backgroundColor: 'rgba(238, 241, 244, 0.1)',
    paddingHorizontal: 10,
    borderRadius: 60,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  defaultBadgeTextMobile: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  expiredBadgeMobile: {
    backgroundColor: 'rgba(255, 62, 62, 0.1)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 60,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  expiredBadgeTextMobile: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  setDefaultTextMobile: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  spacerMobile: { height: 20 },
});

export default PaymentMethodCard;
